import { StudyMetadataTable } from '@/pages/Studies/components';
import { Study } from '@/types';
import { AvatarGroup } from '@nautilusbio/nb-design-system/AvatarGroup';
import { Grid, GridItem } from '@nautilusbio/nb-design-system/Grid';
import { Loading } from '@nautilusbio/nb-design-system/Loading';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import React, { type FC, useState } from 'react';

import studyStyles from '../../Studies/styles.module.css';
import styles from '../styles.module.css';

type StudyInfoSectionProps = {
  data: Study;
  isLoading: boolean;
  t: TFunction<'studies', undefined>;
  emptyTxt: string;
};

export const StudyInfoSection: FC<StudyInfoSectionProps> = ({ data, isLoading, t, emptyTxt }) => {
  const [isSamplesPopoverOpen, setIsSamplesPopoverOpen] = useState(false);
  const assays = data?.assays ?? [];

  return (
    <Grid alignItems="center" fullWidth gridTemplateColumns="auto 1fr">
      <Stack alignItems="start" className={studyStyles.studyContainer} gap={4} orientation="vertical">
        {data?.name && (
          <Text as="h1" className={styles.studyName}>
            {data.name}
          </Text>
        )}
        {data?.description && (
          <Text as="span" className={styles.studyDescription}>
            {data.description}
          </Text>
        )}

        <StudyMetadataTable emptyText={emptyTxt} study={data} />

        {data?.allowed_users?.length > 3 ? (
          <AvatarGroup
            avatars={data?.allowed_users?.map(user => ({
              children: user?.full_name,
              key: user?.id,
            }))}
            limit={5}
          />
        ) : null}
      </Stack>

      <Grid
        alignItems="start"
        className={clsx(studyStyles.studyContainer, styles.sideInfo)}
        columns={5}
        fullWidth
        gap={4}
        justifyContent="center"
        justifyItems="center"
      >
        {(data?.num_samples > 0 || data?.num_instrument_runs > 0) && (
          <>
            {data?.num_instrument_runs !== undefined && data?.num_instrument_runs > 0 && (
              <GridItem>
                <Text noWrap>{t('study.instrument_runs_label')}</Text>
                <Text as="div" size="xl2">
                  {isLoading ? (
                    <Loading className={studyStyles.isRefetching} type="inline" />
                  ) : (
                    data.num_instrument_runs
                  )}
                </Text>
              </GridItem>
            )}

            <GridItem aria-hidden className={styles.sideInfoSpacer} />

            {data?.num_samples !== undefined && data?.num_samples > 0 && (
              <Popover
                content={
                  <Stack className={styles.samplesPopover} gap={1} orientation="vertical">
                    {assays.map((assay, index) => {
                      const assayLabel = assay.assay_version ?? `${t('columnHeaders.assay')} ${index + 1}`;

                      return (
                        <Text key={`${assayLabel}-${index}`} size="sm">
                          <Text as="span" size="sm" weight="bold">
                            {assayLabel}:
                          </Text>{' '}
                          {assay.num_samples}
                        </Text>
                      );
                    })}
                  </Stack>
                }
                data-testid="samples-breakdown-popover"
                isOpen={isSamplesPopoverOpen}
                onClickOutside={() => setIsSamplesPopoverOpen(false)}
                padding={0}
                positions={['bottom']}
                styled
                showArrow
              >
                <GridItem className={styles.samplesGridItem}>
                  {/* Plain <button> avoids DS Button quirks while retaining keyboard/focus accessibility */}
                  <button
                    aria-expanded={isSamplesPopoverOpen}
                    aria-haspopup="dialog"
                    className={styles.samplesGridItemBtn}
                    data-testid="samples-breakdown-trigger"
                    type="button"
                    onBlur={() => setIsSamplesPopoverOpen(false)}
                    onClick={() => setIsSamplesPopoverOpen(prev => !prev)}
                    onFocus={() => setIsSamplesPopoverOpen(true)}
                    onMouseEnter={() => setIsSamplesPopoverOpen(true)}
                    onMouseLeave={() => setIsSamplesPopoverOpen(false)}
                  >
                    <Text noWrap>{t('study.samples_label')}</Text>
                    <Text as="div" size="xl2">
                      {isLoading ? <Loading className={studyStyles.isRefetching} type="inline" /> : data.num_samples}
                    </Text>
                  </button>
                </GridItem>
              </Popover>
            )}

            {/*TBD: Sample Groups*/}
            <GridItem aria-hidden className={styles.sideInfoSpacer} />

            <GridItem>
              <Text noWrap>{t('study.sample_groups')}</Text>
              <Text as="div" size="xl2">
                {isLoading ? <Loading className={studyStyles.isRefetching} type="inline" /> : data.num_sample_groups}
              </Text>
            </GridItem>
          </>
        )}
      </Grid>
    </Grid>
  );
};
